import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { IconButton } from '@wapharma/design-system';
import {
  IconX,
  IconCircleCheck,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';
import styles from './ToastContext.module.css';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export type ToastAction = {
  label: string;
  onClick: () => void;
};

export type ShowToastOptions = {
  tone?: ToastTone;
  details?: string;
  action?: ToastAction;
  /** Default 4200. Set `false` to only dismiss via action. */
  autoDismissMs?: number | false;
};

type ToastItem = {
  id: number;
  title: string;
  details?: string;
  tone: ToastTone;
  primaryAction: ToastAction | null;
  phase: 'enter' | 'exit';
};

type ToastContextValue = {
  showToast: (message: string, toneOrOptions?: ToastTone | ShowToastOptions) => void;
  dismissToast: (id: number) => void;
  clearToasts: () => void;
};

const Ctx = createContext<ToastContextValue | null>(null);

const DEFAULT_TOAST_MS = 4000;
const EXIT_ANIM_MS = 150;

function ToneIcon({ tone }: { tone: ToastTone }) {
  const size = 20;
  const stroke = 1.8;
  const props = { size, stroke, 'aria-hidden': true as const };
  switch (tone) {
    case 'success':
      return <IconCircleCheck {...props} />;
    case 'error':
      return <IconAlertTriangle {...props} />;
    case 'warning':
      return <IconAlertTriangle {...props} />;
    case 'info':
      return <IconInfoCircle {...props} />;
  }
}

function toneClass(tone: ToastTone) {
  switch (tone) {
    case 'success': return styles.tone_success;
    case 'error': return styles.tone_error;
    case 'warning': return styles.tone_warning;
    case 'info': return styles.tone_info;
  }
}

const MAX_QUEUE = 4;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastItem | null>(null);
  const removeTimerRef = useRef<number | null>(null);
  const autoDismissRef = useRef<number | null>(null);
  /** Pending toasts waiting for the current one to clear (FIFO). */
  const queueRef = useRef<ToastItem[]>([]);
  const autoDismissByIdRef = useRef<Map<number, number | false>>(new Map());

  const presentRef = useRef<(item: ToastItem) => void>(() => {});
  const dismissRef = useRef<() => void>(() => {});

  const clearTimers = useCallback(() => {
    if (autoDismissRef.current != null) { window.clearTimeout(autoDismissRef.current); autoDismissRef.current = null; }
  }, []);

  const present = useCallback((item: ToastItem) => {
    clearTimers();
    setToast(item);
    const autoDismissMs = autoDismissByIdRef.current.get(item.id) ?? DEFAULT_TOAST_MS;
    autoDismissByIdRef.current.delete(item.id);
    if (autoDismissMs !== false) {
      autoDismissRef.current = window.setTimeout(() => {
        dismissRef.current();
      }, autoDismissMs);
    }
  }, [clearTimers]);
  presentRef.current = present;

  const dismissToast = useCallback(() => {
    clearTimers();
    setToast((prev) => (prev ? { ...prev, phase: 'exit' } : null));
    if (removeTimerRef.current != null) window.clearTimeout(removeTimerRef.current);
    removeTimerRef.current = window.setTimeout(() => {
      removeTimerRef.current = null;
      const next = queueRef.current.shift();
      if (next) {
        presentRef.current(next);
      } else {
        setToast(null);
      }
    }, EXIT_ANIM_MS);
  }, [clearTimers]);
  dismissRef.current = dismissToast;

  const clearToasts = useCallback(() => {
    clearTimers();
    queueRef.current = [];
    autoDismissByIdRef.current.clear();
    if (removeTimerRef.current != null) {
      window.clearTimeout(removeTimerRef.current);
      removeTimerRef.current = null;
    }
    setToast(null);
  }, [clearTimers]);

  const showToast = useCallback(
    (message: string, toneOrOptions?: ToastTone | ShowToastOptions) => {
      const opts: ShowToastOptions =
        typeof toneOrOptions === 'object' && toneOrOptions !== null && !Array.isArray(toneOrOptions)
          ? (toneOrOptions as ShowToastOptions)
          : { tone: typeof toneOrOptions === 'string' ? (toneOrOptions as ToastTone) : 'success' };

      const id = Date.now() + Math.random();
      const tone = opts.tone ?? 'success';
      // Errors stick until dismissed by default so they can't be silently lost.
      const autoDismissMs = opts.autoDismissMs ?? (tone === 'error' || tone === 'warning' ? false : DEFAULT_TOAST_MS);
      const details = opts.details;
      const primaryAction: ToastAction | null = opts.action ?? null;
      const item: ToastItem = { id, title: message, details, tone, primaryAction, phase: 'enter' };
      autoDismissByIdRef.current.set(id, autoDismissMs);

      setToast((current) => {
        if (!current) {
          return item;
        }
        // Something is already visible. Never silently overwrite an error/warning
        // the user hasn't acknowledged: queue the newcomer instead.
        const isCurrentSticky = current.tone === 'error' || current.tone === 'warning';
        const dup = queueRef.current.some((q) => q.title === message && q.tone === tone);
        if (isCurrentSticky) {
          if (!dup && queueRef.current.length < MAX_QUEUE) queueRef.current.push(item);
          return current;
        }
        // Current is transient (success/info): queue the newcomer and let the
        // current one finish its exit, so messages aren't dropped.
        if (!dup && queueRef.current.length < MAX_QUEUE) queueRef.current.push(item);
        dismissRef.current();
        return current;
      });
    },
    [],
  );

  // Start auto-dismiss timer whenever a toast becomes visible (not when queued via present()).
  useEffect(() => {
    if (!toast || toast.phase === 'exit') return;
    const autoDismissMs = autoDismissByIdRef.current.get(toast.id);
    if (autoDismissMs === undefined) return;
    autoDismissByIdRef.current.delete(toast.id);
    if (autoDismissMs === false) return;
    clearTimers();
    autoDismissRef.current = window.setTimeout(() => {
      dismissRef.current();
    }, autoDismissMs);
  }, [toast, clearTimers]);

  const value: ToastContextValue = {
    showToast,
    dismissToast: () => dismissToast(),
    clearToasts,
  };

  return (
    <Ctx.Provider value={value}>
      {children}
      {toast ? (
        <div
          role={toast.tone === 'error' || toast.tone === 'warning' ? 'alert' : 'status'}
          aria-live={toast.tone === 'error' || toast.tone === 'warning' ? 'assertive' : 'polite'}
          aria-atomic="true"
          className={styles.container}
        >
          <div className={[styles.toastMotion, toast.phase === 'exit' ? styles.exit : styles.enter].join(' ')}>
            <div className={[styles.toast, toneClass(toast.tone)].join(' ')}>
              <div className={styles.body}>
                <div className={styles.iconRow}>
                  <ToneIcon tone={toast.tone} />
                </div>
                <div className={styles.title}>{toast.title}</div>
                {toast.details ? <div className={styles.details}>{toast.details}</div> : null}
                {toast.primaryAction ? (
                  <div className={styles.actionsRow}>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={() => {
                        toast.primaryAction!.onClick();
                        dismissToast();
                      }}
                    >
                      {toast.primaryAction.label}
                    </button>
                  </div>
                ) : null}
              </div>
              <IconButton
                aria-label="Fermer"
                variant="ghost"
                size="xs"
                className={styles.closeBtn}
                onClick={() => dismissToast()}
              >
                <IconX size={14} />
              </IconButton>
            </div>
          </div>
        </div>
      ) : null}
    </Ctx.Provider>
  );
}

export function useToast(): ToastContextValue {
  const v = useContext(Ctx);
  if (!v) {
    return {
      showToast: () => {},
      dismissToast: () => {},
      clearToasts: () => {},
    };
  }
  return v;
}
